/**
 * A task card's title: click to rename in place.
 *
 * Only `TaskCard` uses this — a saved task's slideout renames it in the record
 * header (see `resolveTitlePropertyName`), so there is no separate title
 * editor there.
 */

import type { FocusEvent, KeyboardEvent, ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Input } from '@/lib/base/forms/input/input';
import { cx } from '@/utils/cx';

import { InlineFieldError } from '../components/InlineFieldError';
import { hasMeaningfulTitle } from '../memo/noteKind';
import { NOOP_SAVE, useInlineFieldEdit } from './useInlineFieldEdit';

interface TaskTitleFieldProps {
  title: string;
  /** Supplied → the title is click-to-edit. Withheld → a static heading. */
  onSave?: (value: string) => Promise<void>;
  /**
   * Supplied (and `onSave` withheld) → the title is a click-to-open
   * affordance for the task itself (e.g. an action card opening the task
   * slideout). Ignored while the title is editable — rename wins; the
   * surface around the card provides its own way into the record then.
   */
  onNavigate?: () => void;
}

const HEADING_CLASS = 'text-sm font-semibold leading-5 text-primary';
const MUTED_HEADING_CLASS =
  'text-sm font-semibold leading-5 text-fg-quaternary';
const READ_AFFORDANCE_CLASS =
  'block w-full text-left rounded-md outline-focus-ring hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2';
// Navigate reads as a link into the record, not as an editable cell: underline
// on hover, no cell-hover background.
const NAVIGATE_AFFORDANCE_CLASS =
  'block max-w-full truncate text-left rounded-md hover:underline outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2';
// Negative margins cancel the Input's chrome exactly, so entering edit shifts
// NOTHING: with the title typography forced on the input (text-sm, 20px line —
// see EDIT_INPUT_CLASS) the `sm` box is 20px + py-2 (16px) = 36px; `-my-2`
// makes it contribute the heading's 20px to layout, the box bleeding into the
// surrounding padding. `-mx-3` cancels the input's `px-3` so the TEXT stays on
// the exact spot the heading text occupied.
const EDIT_WRAPPER_CLASS = '-my-2 -mx-3 flex flex-col gap-1';
// Match the heading's typography inside the editor. `sm:text-sm` is required
// alongside the base class: the size scale's root is `text-sm sm:text-md`, and
// tailwind-merge resolves responsive variants as a separate group — a bare
// `text-sm` would leave `sm:text-md` to win at desktop widths.
const EDIT_INPUT_CLASS = 'text-sm sm:text-sm font-semibold';

export function TaskTitleField({
  title,
  onSave,
  onNavigate,
}: TaskTitleFieldProps): ReactElement | null {
  const { isEditing, isSaving, error, startEditing, cancelEditing, commit } =
    useInlineFieldEdit(onSave ?? NOOP_SAVE);
  const [draft, setDraft] = useState(title);
  const readAffordanceRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasEditingRef = useRef(false);
  // Only an explicit keyboard exit (Enter submit / Escape cancel) should pull
  // focus back to the read affordance. A blur commit means focus has already
  // moved to wherever the user tabbed/clicked — restoring it there would steal
  // focus from that destination.
  const restoreFocusOnCloseRef = useRef(false);

  const editable = !!onSave;
  const meaningful = hasMeaningfulTitle(title);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (wasEditingRef.current && !isEditing && restoreFocusOnCloseRef.current) {
      readAffordanceRef.current?.focus();
    }
    if (!isEditing) {
      restoreFocusOnCloseRef.current = false;
    }
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  const handleStartEditing = useCallback(() => {
    setDraft(title);
    startEditing();
  }, [title, startEditing]);

  const handleCommit = useCallback(() => {
    const v = draft.trim();
    if (v === '' || v === (title ?? '').trim()) {
      cancelEditing();
      return;
    }
    void commit(v).then(outcome => {
      // Roll the draft back to the current server title ONLY on a real
      // rejection (the editor stays open showing the persisted value rather
      // than the rejected text). An `'ignored'` outcome means a save for this
      // edit is already in flight — e.g. Enter fires commit, then the blur it
      // triggers fires a second commit — so the draft must be left as the user
      // typed it; resetting it here would erase in-progress input even though
      // the first request is still on track to succeed.
      if (outcome === 'failed') {
        setDraft(title);
      }
    });
  }, [draft, title, cancelEditing, commit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        // Stop the key from bubbling to an enclosing surface (a slideout or
        // an action card treats Enter/Escape as its own shortcuts).
        e.preventDefault();
        e.stopPropagation();
        restoreFocusOnCloseRef.current = true;
        handleCommit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        restoreFocusOnCloseRef.current = true;
        cancelEditing();
      }
    },
    [handleCommit, cancelEditing]
  );

  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLDivElement>) => {
      restoreFocusOnCloseRef.current = false;
      handleCommit();
    },
    [handleCommit]
  );

  if (isEditing) {
    return (
      <div
        className={EDIT_WRAPPER_CLASS}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        <Input
          ref={inputRef}
          aria-label='Title'
          value={draft}
          onChange={setDraft}
          inputClassName={EDIT_INPUT_CLASS}
          // Freeze the field while its save is in flight so a slower save can't
          // be overtaken by a newer keystroke that the in-flight commit would
          // then drop when it closes the editor.
          isReadOnly={isSaving}
        />
        <InlineFieldError message={error} />
      </div>
    );
  }

  if (!editable) {
    if (onNavigate) {
      // The read-only title is the card's only open affordance, so it must
      // stay clickable even for a titleless task — otherwise the preview is a
      // dead end. Fall back to a muted placeholder label when there is no
      // meaningful title.
      return (
        <AriaButton
          aria-label={meaningful ? `Open ${title}` : 'Open task'}
          onPress={onNavigate}
          className={cx(
            meaningful ? HEADING_CLASS : MUTED_HEADING_CLASS,
            NAVIGATE_AFFORDANCE_CLASS
          )}
        >
          {meaningful ? title : 'Untitled task'}
        </AriaButton>
      );
    }
    if (!meaningful) {
      return null;
    }
    return <h3 className={HEADING_CLASS}>{title}</h3>;
  }

  return (
    <AriaButton
      ref={readAffordanceRef}
      aria-label={meaningful ? 'Edit title' : 'Add a title'}
      onPress={handleStartEditing}
      className={cx(
        meaningful ? HEADING_CLASS : MUTED_HEADING_CLASS,
        READ_AFFORDANCE_CLASS
      )}
    >
      {meaningful ? title : 'Add a title'}
    </AriaButton>
  );
}
